// @ai-generated 2026-09-07T00:00:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { useStartWorkflowRun } from '@Hooks/AgenticOrchestrator/useStartWorkflowRun';
import { type ApiWorkflowStoryStep, EnumWorkflowStoryMode } from '@Model';
import { Button, Card, FlexColumn, FlexRow, Modal } from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { WorkflowStepList } from './WorkflowStepList';

/** Wide enough for a step row to read on one line, narrow enough to stay a dialog. */
const BODY_WIDTH = '48rem';

export interface WorkflowRunStartModalProps {
    isOpen: boolean;
    onClose: () => void;
    workflowDefinitionId: string;
    /** The workflow's title, for the dialog header. */
    title: string;
    steps: ApiWorkflowStoryStep[];
    /** Called once the run has been accepted. */
    onStarted: () => void;
}

/**
 * Confirms a workflow run before it starts, with what the run will do to look at. A run started here
 * cannot stop to ask, so whatever it asks is confirmed as it is asked — this dialog is that
 * confirmation.
 */
export function WorkflowRunStartModal({
    isOpen,
    onClose,
    workflowDefinitionId,
    title,
    steps,
    onStarted,
}: WorkflowRunStartModalProps) {
    const { startWorkflow, isStartPending } = useStartWorkflowRun();

    const start = useCallback(async () => {
        const started = await startWorkflow(workflowDefinitionId);
        if (started?.data === undefined) {
            // Reported already by the global request handler; the dialog stays open for a retry.
            return;
        }

        onClose();
        onStarted();
    }, [onClose, onStarted, startWorkflow, workflowDefinitionId]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${title} starten`}
            footer={
                <FlexRow justify="center" gap="sm" className="pt-5">
                    <Button appearance="outlined" variant="normal" onClick={onClose} label="Abbrechen">
                        Abbrechen
                    </Button>
                    <Button onClick={start} locked={isStartPending}>
                        {isStartPending ? 'Workflow wird gestartet' : 'Workflow starten'}
                    </Button>
                </FlexRow>
            }
        >
            {/* Fixed width: the modal sizes itself to its content, so unfolding the steps would
                otherwise widen the dialog under the cursor. */}
            <FlexColumn gap="sm" width={BODY_WIDTH}>
                <Card name="workflow_steps" label="Workflow Details" closed maxWidth={BODY_WIDTH}>
                    <WorkflowStepList steps={steps} mode={EnumWorkflowStoryMode.Preview} />
                </Card>
            </FlexColumn>
        </Modal>
    );
}
